import './augment';
import './augment-var';

import { UIBackground, UIClassesSpec } from './types';
import { UIViewAddSpec, UIViewSpec, UIViewTag } from './view';
import { YLocationParams, $location } from '../y/location';
import { UIParagraphFormat, $formats } from '../../config/formats';
import { UIIcon } from '../../config/icons';
import { $log } from '../y/debug';
import { YI18NString, the_language } from '../y/i18n';
import { uiSetBG } from './bg';
import { yPathToUrl } from '../y/path';

interface UIExternalLink {
    href: string;
    target?: undefined;
    action?: undefined;
    alt?: string;
    download?: string;
    here?: boolean;
    opener?: boolean;
}

interface UILocationLink {
    href?: undefined;
    target?: undefined;
    action?: undefined;
    path: string[];
    params?: YLocationParams;
    alt?: string;
    opener?: boolean;
}

interface UIScrollLink {
    href?: undefined;
    target: string;
    action?: undefined;
    alt?: string;
    opener?: boolean;
}

interface UIActionLink {
    href?: undefined;
    target?: undefined;
    action: () => void;
    alt?: string;
    opener?: undefined;
}

export type UILink = string | string[] | UILocationLink | UIExternalLink | UIScrollLink | UIActionLink;

export namespace $el {
    let next_elem_id = 1;

    export function newId() {
        return 'ui-elem-' + (next_elem_id++);
    }

    function joinClasses(classes: (UIClassesSpec | undefined | false)[]) {
        let join = '';

        for(const c of classes) {
            if(c) {
                if(typeof c === 'string') {
                    join += ' ';
                    join += c;
                } else {
                    join += joinClasses(c);
                }
            }
        }

        return join;
    }

    export function flattenClasses(classes: UIClassesSpec | undefined) {
        if(!classes)
            return undefined;

        if(typeof classes === 'string')
            return classes;

        const join = joinClasses(classes);
        return join ? join.substring(1) : undefined;
    }

    export function splitClasses(classes: string | undefined) {
        if(!classes)
            return [];

        return classes.split(' ');
    }

    export function create<K extends keyof HTMLElementTagNameMap>(tag: K, classes?: UIClassesSpec, content?: UIViewAddSpec): HTMLElementTagNameMap[K];
    export function create(tag: string, classes?: UIClassesSpec, content?: UIViewAddSpec): HTMLElement;
    export function create(tag: string, classes?: UIClassesSpec, content?: UIViewAddSpec): HTMLElement {
        const elem = document.createElement(tag);
        Object.defineProperty(elem, 'ui_elem', { configurable: true, enumerable: true, value: elem });

        if(classes) {
            if(typeof classes === 'string') {
                elem.className = classes;
            } else {
                const join = joinClasses(classes);
                if(join)
                    elem.className = join.substring(1);
            }
        }

        if(content)
            elem.ui_add(content);

        return elem;
    }

    export function registerAnchor(where: UIViewSpec, name: string) {
        const elem = where.ui_elem;
        elem.classList.add('x-anchor');
        elem.ui_anchor_name = name;
    }

    export function scrollToAnchor(name: string) {
        for(const elem of document.querySelectorAll('.x-anchor')) {
            if(elem.ui_anchor_name == name) {
                elem.scrollIntoView({ behavior: 'smooth' });
                return;
            }
        }
    }

    export function A(link: UILink, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        const a = create('a', classes, content);

        if(typeof link == 'string')
            link = { href: link };
        else if(Array.isArray(link))
            link = { path: link };

        if(link.alt)
            a.setAttribute('aria-label', link.alt);
        if(!link.opener)
            a.rel = 'noopener';

        if(link.href != undefined) {
            a.href = link.href;
            a.target = link.here ? '_self' : '_blank';
            if(link.download)
                a.download = link.download;
        } else if(link.target != undefined) {
            const target = link.target;
            a.addEventListener('click', e => {
                e.preventDefault();
                $log('app', 'should scroll to', target)
                scrollToAnchor(target);
            });
        } else if(link.action != undefined) {
            a.addEventListener('click', e => {
                e.preventDefault();
                link.action();
            });
        } else {
            const path = [...link.path];
            const params = link.params;
            a.href = yPathToUrl(path);
            a.addEventListener('click', e => {
                e.preventDefault();
                $location.navigate(path, params);
            });
        }

        return a;
    }

    export function DIV(classes?: UIClassesSpec, content?: UIViewAddSpec) {
        return create('div', classes, content);
    }

    export function SECTION(classes?: UIClassesSpec, content?: UIViewAddSpec) {
        return create('section', ['ui-section', classes], content);
    }

    export function FORM(classes?: UIClassesSpec, content?: UIViewAddSpec): HTMLElement {
        return create('form', classes, content);
    }

    export function BR(classes?: UIClassesSpec) {
        return create('br', classes);
    }

    export function SPAN(classes?: UIClassesSpec, content?: UIViewAddSpec) {
        return create('span', classes, content);
    }

    export function B(content: UIViewAddSpec) {
        return create('div', undefined, content);
    }

    export function I(content: UIViewAddSpec) {
        return create('span', undefined, content);
    }

    export function BACK(background: UIBackground, classes?: UIClassesSpec) {
        const elem = create('div', classes);
        uiSetBG(elem, background);
        return elem;
    }

    export function H1(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('h1', [classes, $formats[format].classes], content);
    }

    export function H2(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('h2', [classes, $formats[format].classes], content);
    }

    export function H3(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('h3', [classes, $formats[format].classes], content);
    }

    export function H4(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('h4', [classes, $formats[format].classes], content);
    }

    export function H5(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('h5', [classes, $formats[format].classes], content);
    }

    export function H6(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('h6', [classes, $formats[format].classes], content);
    }

    export function P(format: UIParagraphFormat, content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('p', [classes, $formats[format].classes], content);
    }

    export function Ps(format: UIParagraphFormat, content: (UIViewAddSpec | undefined)[], classes?: UIClassesSpec) {
        const ps: HTMLElement[] = [];
        for(const c of content) {
            c && ps.push(create('p', [classes, $formats[format].classes], c));
        }
        return ps;
    }

    export function LABEL(content?: UIViewAddSpec, classes?: UIClassesSpec) {
        return create('label', classes, content);
    }

    export function IMG(src?: string, classes?: UIClassesSpec, alt?: string, width?: number, height?: number) {
        const img = create('img', classes);
        if(src)
            img.src = src;
        if(alt)
            img.alt = alt;
        if(width)
            img.width = width;
        if(height)
            img.height = height;
        return img;
    }

    // TODO
    // export function newIMG(img: UIImage, classes?: UIClassesSpec)
    // {
    //     // return IMG(img.src, [classes, img.classes], img.alt, img.width, img.height);
    // }

    export function CANVAS(width: number, height: number) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        return canvas;
    }

    export function INPUT(type: string, classes?: UIClassesSpec) {
        const input = create('input', classes);
        input.type = type;
        return input;
    }

    export function TEXTAREA(classes?: UIClassesSpec) {
        const area = create('textarea', classes);
        return area;
    }

    export function ICON(which: UIIcon, classes?: UIClassesSpec, link?: UILink) {
        let weight_class: string;

        if(which & UIIcon.MASK_BRAND) {
            weight_class = 'k-fab';
        } else {
            const weight = which & UIIcon.MASK_WEIGHT;
            if(weight === UIIcon.WEIGHT_SOLID)
                weight_class = 'k-fas';
            else
                weight_class = 'k-fa';
        }

        classes = [classes, weight_class, 'ui-icon ui-icon-' + (which & UIIcon.MASK_N)];
        return link ? A(link, undefined, classes) : create('i', classes);
    }

    export function HANDLE() {
        return ICON(UIIcon.BARS, 'u-handle');
    }

    export function RULER(classes?: UIClassesSpec) {
        return create('hr', classes);
    }

    export function S(grow?: number, classes?: UIClassesSpec) {
        const el = create('div', ['s-flex', classes]);
        if(grow)
            el.style.flexGrow = grow as any;
        return el;
    }

    export function REQUIRED() {
        return create('span', 'ui-required-star', '*');
    }

    export function IFRAME(src: string, classes?: UIClassesSpec) {
        const el = create('iframe', classes);
        el.src = src;
        return el;
    }

    function makeTableRow(content: UIViewAddSpec[], tag: 'th' | 'td') {
        const tr = create('tr');
        for(const c of content) {
            if(c instanceof HTMLTableCellElement)
                tr.appendChild(c);
            else
                tr.appendChild(create(tag, undefined, c));
        }
        return tr;
    }

    export function TABLE(headers?: UIViewAddSpec[], rows?: UIViewAddSpec[][], classes?: UIClassesSpec) {
        let table = create('table', classes);
        if(headers) {
            table.appendChild(create('thead', undefined, makeTableRow(headers, 'th')));
        }
        if(rows) {
            let tbody = create('tbody');
            for(const r of rows) {
                tbody.appendChild(makeTableRow(r, 'td'));
            }
            table.appendChild(tbody);
        }

        return table;
    }

    export function UL(items: UIViewAddSpec[], classes?: UIClassesSpec) {
        let ul = create('ul', classes);

        for(const i of items) {
            if(i instanceof HTMLLIElement)
                ul.appendChild(i);
            else
                ul.appendChild(create('li', undefined, i));
        }

        return ul;
    }

    export function OL(items: UIViewAddSpec[], classes?: UIClassesSpec) {
        let ol = create('ol', classes);

        for(const i of items) {
            if(i instanceof HTMLLIElement)
                ol.appendChild(i);
            else
                ol.appendChild(create('li', undefined, i));
        }

        return ol;
    }

    export function VIDEO(src?: string, classes?: UIClassesSpec) {
        const el = create('video', classes);
        if(src) {
            const source = create('source');
            source.src = src;
            el.appendChild(source);
        }

        return el;
    }

    export function AUDIO(src?: string, classes?: UIClassesSpec) {
        const el = create('audio', classes);
        if(src) {
            const source = create('source');
            source.src = src;
            el.appendChild(source);
        }

        return el;
    }

    export function fromEditedHtml(html: YI18NString, classes?: UIClassesSpec, tag: UIViewTag = 'div') {
        const content = create(tag, [classes, 'x-edited']);
        content.innerHTML = html[the_language] || html['sv'] || '';
        for(const el of content.querySelectorAll('a.u-link')) {
            const href = el.getAttribute('href');
            $log('app', 'href is', href);
            if(href?.startsWith('#')) {
                const target = href.substring(1);
                $log('app', 'target is', target);
                el.addEventListener('click', e => {
                    $log('app', 'target is', target);
                    e.preventDefault();
                    $log('app', 'scrolling to', target);
                    scrollToAnchor(target);
                });
                el.removeAttribute('href');
            }
        }

        return content;
    }
}

$log('app', 'initializing UI elements');
